/**
 * Wires Firebase web push into the React tree (mounted once in App.tsx).
 *
 * Runs only in a real browser and only while signed in — inside the Expo WebView the native
 * shell registers the FCM device instead (AuthContext → postToShell). A foreground push shows a
 * toast (with a "View" action for its deep link) and refreshes the notification inbox + unread
 * badge. For a pharmacist, a new order (`order.placed`) instead goes straight into the cached
 * queue lists and pops up top-right with View / Ignore (NewOrderPopup). A click on a background
 * notification is routed here by the service worker (`push.open` message) so it navigates inside
 * the SPA.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import type { OrderSummary } from "../api/orders";
import { isInWebView } from "../bridge/webview";
import { useAuth } from "../auth/AuthContext";
import { addToQueueCache, orderFromPush } from "../lib/newOrders";
import { PUSH_RETRY_EVENT, pushOnHere, setupWebPush, watchPushPermission } from "../lib/push";
import { NEW_ORDER_TOASTER, NewOrderCard } from "./NewOrderPopup";

// How long an unanswered new-order popup stays up (sonner pauses it while hovered).
const NEW_ORDER_POPUP_MS = 5000;

export function PushNotifications() {
  const { isAuthenticated, user } = useAuth();
  const isPharmacist = user?.role === "pharmacist";
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Read through refs inside the push setup below: `navigate` is a new function after every
  // route change, and re-running the setup on each navigation would drop pushes arriving while
  // it re-subscribes (and re-register the device each time).
  const navigateRef = useRef(navigate);
  const isPharmacistRef = useRef(isPharmacist);
  const userIdRef = useRef(user?.id);
  useEffect(() => {
    navigateRef.current = navigate;
    isPharmacistRef.current = isPharmacist;
    userIdRef.current = user?.id;
  });

  // Set push up again once the user allows notifications later — from the profile panel
  // (PUSH_RETRY_EVENT) or by unblocking the site in the browser's settings.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!isAuthenticated || isInWebView()) return;
    const retry = () => setAttempt((n) => n + 1);
    window.addEventListener(PUSH_RETRY_EVENT, retry);
    const unwatch = watchPushPermission((permission) => permission === "granted" && retry());
    return () => {
      window.removeEventListener(PUSH_RETRY_EVENT, retry);
      unwatch();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    // Not when the user switched notifications off for this browser (profile panel).
    if (!isAuthenticated || isInWebView() || !pushOnHere()) return;

    let unsubscribe = () => {};
    let cancelled = false;

    const showNewOrder = (order: OrderSummary) => {
      addToQueueCache(qc, order);
      toast.custom(
        (id) => (
          <NewOrderCard
            order={order}
            onIgnore={() => toast.dismiss(id)}
            onView={() => {
              toast.dismiss(id);
              // The order page's own flow (claim → confirm address → price) takes it from here.
              navigateRef.current(`/staff/pharmacist/orders/${order.id}`);
            }}
          />
        ),
        { id: `new-order-${order.id}`, toasterId: NEW_ORDER_TOASTER, duration: NEW_ORDER_POPUP_MS },
      );
    };

    setupWebPush({
      userId: userIdRef.current,
      onForeground: (payload) => {
        const data = payload.data ?? {};
        const newOrder = isPharmacistRef.current && data.event_code === "order.placed" ? orderFromPush(data) : null;
        if (newOrder) {
          showNewOrder(newOrder);
        } else {
          const title =
            data.title ?? payload.notification?.title ?? "New notification";
          const link = data.deep_link;
          toast(title, {
            description: data.body ?? payload.notification?.body,
            action: link ? { label: "View", onClick: () => navigateRef.current(link) } : undefined,
          });
        }
        qc.invalidateQueries({ queryKey: ["notifications"] });
        qc.invalidateQueries({ queryKey: ["notif-unread"] });
        // A reminder just went off: the Reminders page shows its next time.
        if (data.event_code === "health.reminder") qc.invalidateQueries({ queryKey: ["health-reminders"] });
      },
    }).then((unsub) => {
      if (cancelled) unsub();
      else unsubscribe = unsub;
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [isAuthenticated, qc, attempt]);

  // Notification clicks from the service worker (public/firebase-messaging-sw.js).
  useEffect(() => {
    if (isInWebView() || !("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const msg = event.data as { type?: string; deep_link?: string } | null;
      if (msg?.type === "push.open" && msg.deep_link) {
        navigate(msg.deep_link);
        qc.invalidateQueries({ queryKey: ["notifications"] });
        qc.invalidateQueries({ queryKey: ["notif-unread"] });
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate, qc]);

  return null;
}
